import { Product } from '@shared/components/ui/Product';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BatchSummary } from '@shared/models/BatchSummary';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineBatchMeta } from './LineBatchMeta';

interface LocatedProduct {
  id: string;
  product_id?: number;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location: string;
  image?: string;
  batch_id?: number | null;
  batch?: BatchSummary | null;
}

interface LocatedProductsSectionProps {
  products: LocatedProduct[];
  onSort: (sortBy: string) => void;
  currentSort: string;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export const LocatedProductsSection: React.FC<LocatedProductsSectionProps> = ({ products, searchQuery = '' }) => {
  const { t } = useTranslation('locationordersDetailModule');

  return (
    <div className="flex flex-col h-full">
      {/* Header - igual que Picking */}
      <h2 className="text-lg font-semibold text-foreground mb-4 flex-shrink-0 flex items-center space-x-2">
        <span className="text-green-600 dark:text-green-400">
          {t('ui.located_products', { defaultValue: 'Productos ubicados' })}
        </span>
        <span className="text-sm text-muted-foreground">({products.length})</span>
      </h2>

      {/* Lista de productos - igual que Picking */}
      <div className="flex-1 overflow-y-auto">
        {products.length > 0 ? (
          <div className="space-y-4">
            {products.map((product) => {
              // 🎯 Verificar si el producto está completado (qty_planned = qty_moved)
              const isCompleted = product.quantity === product.totalQuantity;

              return (
                <div
                  key={product.id}
                  className="border-b pb-4 bg-green-50/30 dark:bg-green-950/30 border-green-200 dark:border-green-800"
                >
                  {/* Badge de ubicación - Solo mostrar si NO está completado */}
                  {!isCompleted && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2">
                        <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-800 inline-flex items-center gap-2">
                          <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3" />
                          {product.location}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Información del producto usando componente Product + cantidades */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <Product
                        id={product.product_id ?? 0}
                        src={product.image || ''}
                        name={product.name}
                        code={product.code}
                        acronym={product.name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                      <LineBatchMeta batch={product.batch} batchId={product.batch_id} />
                    </div>

                    {/* Cantidades */}
                    <div className="flex flex-col items-end space-y-1 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {product.quantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{product.totalQuantity}</span>
                      </div>

                      {/* Barra de progreso completada */}
                      <div className="w-16 h-2 bg-green-200 dark:bg-green-800 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: '100%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center h-full">
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {searchQuery && searchQuery.trim()
                  ? t('items.search.no_results.title')
                  : t('ui.no_located_products', { defaultValue: 'No hay productos ubicados' })}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
